import { useEffect, useRef } from "react";
import { useTourStoreApi } from "@heroncendre/player";
import { PAS_CLAVIER_DEG } from "./FlechesRotation.js";
import { dansUnChamp } from "./RaccourciEdition.js";

/**
 * ============================================================================
 *  useRotationVisiteur.ts — Le visiteur a-t-il tourné la vue lui-même ?
 * ----------------------------------------------------------------------------
 *  Le cap du store ne suffit pas à le dire : la rotation au repos, l'accueil,
 *  les plans de la voix off et l'oscillation de l'indicateur l'écrivent aussi.
 *  Un cap qui bouge pendant que le doigt est posé sans glisser ferait croire à
 *  un geste qui n'a pas eu lieu.
 *
 *  On mesure donc le GESTE, converti en degrés comme le fait le moteur
 *  (`CameraRig` : FOV / hauteur du canvas par pixel) — c'est exactement la
 *  rotation qu'il produit. Écoute en phase de capture sur le document : le
 *  moteur capture le pointeur sur son canvas, et la couche d'interface ne doit
 *  rien intercepter.
 *
 *  Deux signaux :
 *   - `onGeste` au premier contact (pointeur posé sur le canvas, flèche) :
 *     l'indicateur arrête aussitôt son animation ;
 *   - `onSeuil` quand le cap parcouru dépasse `seuilDeg` : il a compris.
 * ==========================================================================*/

export interface RotationVisiteurOptions {
  actif: boolean;
  /** Les flèches comptent-elles ? Seulement là où elles tournent la vue (`FlechesRotation`). */
  clavier: boolean;
  seuilDeg: number;
  onGeste: () => void;
  onSeuil: () => void;
}

export function useRotationVisiteur({ actif, clavier, seuilDeg, onGeste, onSeuil }: RotationVisiteurOptions) {
  const store = useTourStoreApi();
  // Les rappels changent à chaque rendu ; l'écoute, non.
  const rappels = useRef({ onGeste, onSeuil });
  rappels.current = { onGeste, onSeuil };

  useEffect(() => {
    if (!actif) return;
    const poses = new Map<number, number>();
    let parcours = 0;
    let atteint = false;

    const ajouter = (degres: number) => {
      parcours += degres;
      if (!atteint && parcours >= seuilDeg) {
        atteint = true;
        rappels.current.onSeuil();
      }
    };

    const surPose = (event: PointerEvent) => {
      if (!(event.target instanceof HTMLCanvasElement)) return;
      poses.set(event.pointerId, event.clientX);
      rappels.current.onGeste();
    };

    const surGlisse = (event: PointerEvent) => {
      const precedent = poses.get(event.pointerId);
      if (precedent === undefined) return;
      poses.set(event.pointerId, event.clientX);
      // Deux doigts : c'est un pincement (zoom), le moteur ne tourne pas.
      if (poses.size > 1) return;
      const canvas = event.target instanceof HTMLCanvasElement ? event.target : null;
      const hauteur = canvas?.clientHeight || window.innerHeight;
      const { view, tour } = store.getState();
      const fov = view.fov ?? tour.settings.defaultFov;
      ajouter(Math.abs(event.clientX - precedent) * (fov / hauteur));
    };

    const surLeve = (event: PointerEvent) => {
      poses.delete(event.pointerId);
    };

    const surTouche = (event: KeyboardEvent) => {
      if (!clavier || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
      // Consommée ailleurs (focus dans le contrôleur d'ambiances) : la vue n'a pas tourné.
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (dansUnChamp(event.target)) return;
      rappels.current.onGeste();
      ajouter(PAS_CLAVIER_DEG);
    };

    document.addEventListener("pointerdown", surPose, true);
    document.addEventListener("pointermove", surGlisse, true);
    document.addEventListener("pointerup", surLeve, true);
    document.addEventListener("pointercancel", surLeve, true);
    window.addEventListener("keydown", surTouche);
    return () => {
      document.removeEventListener("pointerdown", surPose, true);
      document.removeEventListener("pointermove", surGlisse, true);
      document.removeEventListener("pointerup", surLeve, true);
      document.removeEventListener("pointercancel", surLeve, true);
      window.removeEventListener("keydown", surTouche);
    };
  }, [actif, clavier, seuilDeg, store]);
}
